<!-- h-full + flex column so the inner grid can flex-1 into the source-
     pane height. Without this, the entries list + edit form flow with
     the page and don't scroll internally when the registry has more
     rows than fit on screen. -->
<div class="p-4 space-y-3 h-full flex flex-col min-h-0">

  <!-- Header row scoped to this source. Source token = the Mongo
       collection path so an operator can go mongosh-verify if needed. -->
  <div class="flex items-center gap-3 flex-wrap">
    <h2 class="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
      <i class="fa-solid fa-list-check text-app-teal-100"></i>Symbol Lists Registry
    </h2>
    <button class="dark-btn dark-btn-sm" (click)="reload()" [disabled]="loading">
      <i class="fa-solid fa-rotate-right mr-1" [class.fa-spin]="loading"></i>Reload
    </button>
    <app-copy-source-button
      [source]="'F2-ADMIN.SymbolLists' + (selectedName ? ('._id=' + selectedName) : '')"
      [tooltip]="selectedName ? ('Mongo doc for the ' + selectedName + ' registry entry') : 'F2-ADMIN.SymbolLists — the registry collection'">
    </app-copy-source-button>
    <span class="text-xs text-gray-500 dark:text-gray-400 ml-2">
      <strong>{{ entries.length }}</strong> entr<span *ngIf="entries.length === 1">y</span><span *ngIf="entries.length !== 1">ies</span> in the registry
    </span>
  </div>

  <p class="text-xs text-gray-500 dark:text-gray-400">
    Each row here is a POINTER to a GitHub file. The bytes stay on GitHub;
    Mongo just carries the settings — which repo, which path, which
    column is the ticker. Add a row to surface a new list without a code
    change. Editing the file contents themselves still lives under
    <strong>GitHub Symbol Lists</strong> above.
  </p>

  <div class="grid grid-cols-1 md:grid-cols-[320px_1fr] gap-4 flex-1 min-h-0">

    <!-- Left: registry entries picker. flex-col so the ul scrolls
         internally when the registry has more entries than fit. -->
    <div class="border border-gray-200 dark:border-[#434863] rounded bg-white dark:bg-app-black-100 overflow-hidden flex flex-col min-h-0">
      <div class="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-[#434863] flex-none">
        <span class="text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">Registry</span>
        <button class="dark-btn dark-btn-sm ml-auto" (click)="startNew()" title="Add a registry entry">
          <i class="fa-solid fa-plus mr-1"></i>New entry
        </button>
      </div>
      <ul *ngIf="entries.length > 0" class="divide-y divide-gray-200 dark:divide-[#434863] flex-1 overflow-y-auto min-h-0">
        <li *ngFor="let e of entries; trackBy: trackByName">
          <div class="flex items-stretch">
            <button
              class="flex-1 text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-app-blue-100/30 transition"
              [class.bg-app-blue-100]="selectedName === e._id"
              (click)="selectEntry(e._id)">
              <div class="flex items-center gap-2">
                <span class="font-mono text-sm text-gray-900 dark:text-white">{{ e._id }}</span>
                <span *ngIf="e.format" class="text-[10px] px-1.5 py-0.5 rounded uppercase font-mono" [ngClass]="formatBadgeClass(e.format)">
                  {{ e.format }}
                </span>
              </div>
              <div class="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                <span *ngIf="e.label">{{ e.label }} · </span>
                <span *ngIf="e.repo" class="font-mono">{{ e.repo }}</span>
                <span *ngIf="e.path" class="font-mono">/{{ e.path }}</span>
              </div>
              <div *ngIf="e.ticker_column" class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                ticker column: <code>{{ e.ticker_column }}</code>
              </div>
              <div *ngIf="e.customer_slug || e.scanner_slug" class="text-[10px] mt-1 flex items-center gap-1 flex-wrap">
                <i class="fa-solid fa-user-shield text-amber-600 dark:text-amber-300"></i>
                <span class="text-amber-700 dark:text-amber-300">scope:</span>
                <span *ngIf="e.customer_slug" class="px-1.5 py-0.5 rounded font-mono bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                  customer {{ e.customer_slug }}
                </span>
                <span *ngIf="e.scanner_slug" class="px-1.5 py-0.5 rounded font-mono bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">
                  scanner {{ e.scanner_slug }}
                </span>
              </div>
              <!-- IT-F2-230: client-access badges — only show when the list
                   is exposed OR editable by client role. Admin defaults are
                   invisible to keep the row quiet for the common case. -->
              <div *ngIf="e.reader_role === 'client' || e.writer_role === 'client'"
                   class="text-[10px] mt-1 flex items-center gap-1 flex-wrap">
                <i class="fa-solid fa-share-nodes text-teal-600 dark:text-teal-300"></i>
                <span class="text-teal-700 dark:text-teal-300">client access:</span>
                <span *ngIf="e.reader_role === 'client'" class="px-1.5 py-0.5 rounded font-mono bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-200" title="Visible in the client app's Common Data section">reader</span>
                <span *ngIf="e.writer_role === 'client'" class="px-1.5 py-0.5 rounded font-mono bg-teal-100 text-teal-800 dark:bg-teal-500/20 dark:text-teal-200" title="Client can add/remove symbols; create+destroy still admin-only">writer</span>
              </div>
              <div *ngIf="e.updated_at" class="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                updated {{ e.updated_at | date:'MMM d, y, h:mm a' }}<span *ngIf="e.updated_by_email"> by <span class="font-mono">{{ e.updated_by_email }}</span></span>
              </div>
            </button>
            <button
              class="px-2 hover:bg-gray-100 dark:hover:bg-app-black-200 border-l border-gray-200 dark:border-[#434863] transition"
              (click)="toggleActiveInList(e)"
              [title]="(e.active !== false) ? 'Active — click to deactivate' : 'Inactive — click to activate'">
              <i class="fa-solid fa-circle text-[10px]"
                 [class.text-green-500]="e.active !== false"
                 [class.text-gray-400]="e.active === false"
                 [class.dark:text-gray-600]="e.active === false"></i>
            </button>
          </div>
        </li>
      </ul>
      <div *ngIf="entries.length === 0 && !loading" class="px-3 py-6 text-center text-xs text-gray-500 dark:text-gray-400">
        No registry entries yet. Click <strong>New entry</strong> to add one, or seed from the legacy
        hardcoded repo via <code>POST /rest/symbol-lists/migrate-from-github</code>.
      </div>
    </div>

    <!-- Right: form. flex-col + min-h-0 so the body scrolls internally
         while the header + save/cancel footer stay pinned. -->
    <div class="border border-gray-200 dark:border-[#434863] rounded bg-white dark:bg-app-black-100 overflow-hidden flex flex-col min-h-0">

      <div *ngIf="mode === 'idle'" class="px-4 py-10 text-center text-sm text-gray-500 dark:text-gray-400">
        Pick an entry on the left to edit, or click <strong>New entry</strong> to add one.
      </div>

      <div *ngIf="mode !== 'idle'" class="flex flex-col flex-1 min-h-0">
        <div class="flex items-center gap-3 px-3 py-2 border-b border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100 flex-none">
          <span class="text-sm font-semibold text-gray-900 dark:text-white">
            <i class="fa-solid" [ngClass]="mode === 'new' ? 'fa-plus' : 'fa-pen-to-square'"></i>
            {{ mode === 'new' ? 'New registry entry' : selectedName }}
          </span>
          <span *ngIf="dirty" class="text-[11px] text-amber-600 dark:text-amber-300 ml-2">
            • unsaved changes
          </span>

          <!-- Copy / Paste JSON — for duplicating entries or sharing a config -->
          <div class="ml-auto flex items-center gap-1">
            <button type="button"
              class="dark-btn dark-btn-sm"
              (click)="copyJson()"
              title="Copy this form's fields as a JSON payload — the same shape POST /rest/symbol-lists/registry accepts.">
              <i class="fa-solid fa-clipboard mr-1"></i>Copy JSON
            </button>
            <button type="button"
              class="dark-btn dark-btn-sm"
              (click)="pasteJson()"
              [title]="mode === 'new' ? 'Paste a JSON payload from clipboard — every field including name populates.' : 'Paste a JSON payload from clipboard — every field EXCEPT name populates (name is immutable on existing entries).'">
              <i class="fa-solid fa-paste mr-1"></i>Paste JSON
            </button>
          </div>
        </div>

        <!-- Form body — flex-1 + overflow-y-auto so long forms (many
             fields + History section) scroll internally instead of
             blowing past the source-pane fold. -->
        <div class="px-4 py-4 space-y-3 flex-1 overflow-y-auto min-h-0">
          <!-- Name (immutable on edit — the _id is the Redis key) -->
          <label class="block">
            <span class="text-xs font-medium text-gray-700 dark:text-gray-300">
              Name <span class="text-red-500">*</span>
            </span>
            <input
              type="text"
              class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm font-mono"
              [(ngModel)]="form.name"
              [readonly]="mode === 'view'"
              [class.opacity-70]="mode === 'view'"
              placeholder="e.g. sp500" />
            <span class="text-[10px] text-gray-500 dark:text-gray-400">
              Also the Redis key: <code>symbol-list:{{ form.name || '&lt;name&gt;' }}</code>. Lowercase, digits, hyphens, underscores only. Immutable after create.
            </span>
          </label>

          <!-- Label -->
          <label class="block">
            <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Label</span>
            <input
              type="text"
              class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm"
              [(ngModel)]="form.label"
              placeholder="e.g. S&P 500" />
            <span class="text-[10px] text-gray-500 dark:text-gray-400">Display name in the SPA — falls back to the raw name if empty.</span>
          </label>

          <!-- Repo -->
          <label class="block">
            <span class="text-xs font-medium text-gray-700 dark:text-gray-300">
              GitHub repo <span class="text-red-500">*</span>
            </span>
            <input
              type="text"
              class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm font-mono"
              [(ngModel)]="form.repo"
              (ngModelChange)="onRepoChange()"
              placeholder="F2Tech-WebDev-v2/common-symbol-lists" />
            <span class="text-[10px] text-gray-500 dark:text-gray-400">Format <code>owner/name</code>. Any repo the service's GITHUB_TOKEN can read.</span>
          </label>

          <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-3">
            <!-- Branch (datalist-backed for dropdown UX; still accepts free text) -->
            <label class="block">
              <span class="text-xs font-medium text-gray-700 dark:text-gray-300">
                Branch
                <span *ngIf="branchOptions.length" class="text-[10px] text-gray-500 dark:text-gray-400 font-normal ml-1">
                  ({{ branchOptions.length }} available)
                </span>
              </span>
              <input
                type="text"
                list="registry-branch-options"
                class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm font-mono"
                [(ngModel)]="form.branch"
                (ngModelChange)="onBranchChange()"
                placeholder="main" />
              <datalist id="registry-branch-options">
                <option *ngFor="let b of branchOptions" [value]="b"></option>
              </datalist>
            </label>

            <!-- Format -->
            <label class="block">
              <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Format</span>
              <select
                class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm"
                [(ngModel)]="form.format">
                <option value="txt">txt — one ticker per line</option>
                <option value="csv">csv — header row + rich metadata</option>
              </select>
            </label>
          </div>

          <!-- Path -->
          <label class="block">
            <div class="flex items-center gap-2">
              <span class="text-xs font-medium text-gray-700 dark:text-gray-300">
                Path in repo <span class="text-red-500">*</span>
                <span *ngIf="pathOptions.length" class="text-[10px] text-gray-500 dark:text-gray-400 font-normal ml-1">
                  ({{ pathOptions.length }} candidate<span *ngIf="pathOptions.length !== 1">s</span>)
                </span>
              </span>
              <button type="button"
                class="text-[10px] text-app-teal-100 hover:underline ml-auto"
                (click)="togglePathShowAll()"
                title="Toggle between lists/*.{txt,csv} and every txt/csv in the repo">
                <i class="fa-solid" [ngClass]="pathShowAll ? 'fa-filter' : 'fa-folder-tree'"></i>
                {{ pathShowAll ? 'lists/ only' : 'show all txt/csv' }}
              </button>
            </div>
            <input
              type="text"
              list="registry-path-options"
              class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm font-mono"
              [(ngModel)]="form.path"
              [placeholder]="form.format === 'csv' ? 'lists/sp500.csv' : 'lists/sp500.txt'" />
            <datalist id="registry-path-options">
              <option *ngFor="let p of pathOptions" [value]="p"></option>
            </datalist>
            <span class="text-[10px] text-gray-500 dark:text-gray-400">Relative to repo root. File format usually mirrors the <code>Format</code> field above.</span>
          </label>

          <!-- Ticker column (csv only) -->
          <label class="block" *ngIf="form.format === 'csv'">
            <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Ticker column</span>
            <input
              type="text"
              class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm font-mono"
              [(ngModel)]="form.ticker_column"
              placeholder="Symbol" />
            <span class="text-[10px] text-gray-500 dark:text-gray-400">
              Case-insensitive header name. Leave blank to fall back to <code>Symbol</code> → <code>Ticker</code> → column 0.
            </span>
          </label>

          <!-- Scope (optional) -->
          <div class="pt-2 border-t border-gray-200 dark:border-[#434863]">
            <div class="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Scope (optional)</div>
            <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-3">
              <label class="block">
                <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Customer slug</span>
                <input
                  type="text"
                  class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm font-mono"
                  [(ngModel)]="form.customer_slug"
                  placeholder="e.g. theo-trade" />
                <span class="text-[10px] text-gray-500 dark:text-gray-400">Restrict this list to a customer. Leave blank for fleet-wide.</span>
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Scanner slug</span>
                <input
                  type="text"
                  class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm font-mono"
                  [(ngModel)]="form.scanner_slug"
                  placeholder="e.g. ghost-prints" />
                <span class="text-[10px] text-gray-500 dark:text-gray-400">Restrict further to a specific scanner. Leave blank for every scanner (within the customer scope if set).</span>
              </label>
            </div>
            <p class="text-[10px] text-red-600 dark:text-amber-300 mt-2">
              <i class="fa-solid fa-triangle-exclamation mr-1"></i>
              <strong>Customer slug is REQUIRED when Reader role = client.</strong> A client-visible list without a customer scope stays invisible to every client app (admin misconfig protection — Mike caught cross-customer leak 2026-08-11). Set this to the exact customer whose client app should see + edit the list. Scanner slug is metadata for now.
            </p>
          </div>

          <!-- IT-F2-230: Client access — per-list VIEW + EDIT role gates.
               Admin defaults keep the list admin-only. Toggling reader_role
               to 'client' exposes it in the f2-clients Common Data section;
               toggling writer_role to 'client' lets client role PATCH the
               file (add/remove symbols). Create + destroy stay admin-only
               regardless. -->
          <div class="pt-2 border-t border-gray-200 dark:border-[#434863]">
            <div class="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Client access</div>
            <div class="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-3">
              <label class="block">
                <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Reader role</span>
                <select [(ngModel)]="form.reader_role"
                        class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm font-mono">
                  <option value="admin">admin — only F2 admins can see this list</option>
                  <option value="client">client — customer sees it in their Common Data</option>
                </select>
                <span class="text-[10px] text-gray-500 dark:text-gray-400">Controls VIEW visibility in the customer app.</span>
              </label>
              <label class="block">
                <span class="text-xs font-medium text-gray-700 dark:text-gray-300">Writer role</span>
                <select [(ngModel)]="form.writer_role"
                        class="mt-1 block w-full border border-gray-300 dark:border-[#434863] bg-white dark:bg-app-black-100 text-gray-900 dark:text-white rounded px-3 py-1.5 text-sm font-mono">
                  <option value="admin">admin — only F2 admins can edit contents</option>
                  <option value="client">client — customer can add/remove symbols</option>
                </select>
                <span class="text-[10px] text-gray-500 dark:text-gray-400">Controls EDIT permission. Create + destroy stay admin-only.</span>
              </label>
            </div>
            <p class="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
              <i class="fa-solid fa-info-circle mr-1"></i>
              Backend guards every write against the caller's role AND their customer entitlement — see the <em>Customer slug</em> field above. Setting <code>writer_role</code> to <em>client</em> implies the customer is allowed to sign changes with their credentials — the audit trail lives in the underlying lists repo's git history.
            </p>
          </div>

          <!-- Active -->
          <label class="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200 dark:border-[#434863]">
            <input type="checkbox" [(ngModel)]="form.active" class="rounded" />
            <span class="text-xs text-gray-700 dark:text-gray-300">
              Active — the refresh pulls this list from GitHub and syncs it to Redis.
              <span class="text-gray-500 dark:text-gray-400 block">Deactivate to hide the list from downstream consumers without deleting the row.</span>
            </span>
          </label>

          <!-- IT-F2-230 Sub 4: History — git commit log for the
               underlying file. Only makes sense in view mode (new mode
               has no file yet). Collapsed by default so we don't burn
               a GitHub API call every time an admin opens a list. -->
          <div *ngIf="mode === 'view' && selectedName" class="pt-2 mt-2 border-t border-gray-200 dark:border-[#434863]">
            <div class="flex items-center justify-between mb-1">
              <button type="button"
                      class="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center gap-1"
                      (click)="toggleHistory()">
                <i class="fa-solid fa-fw" [ngClass]="historyExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                History
                <span *ngIf="historyLoadedFor === selectedName && !historyLoading" class="ml-1 text-gray-400 dark:text-gray-500 normal-case font-normal">
                  · {{ history.length }} commit{{ history.length === 1 ? '' : 's' }} (last {{ history.length < 50 ? history.length : 50 }})
                </span>
              </button>
              <button *ngIf="historyExpanded" type="button"
                      class="text-[10px] text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      (click)="refreshHistory()"
                      [disabled]="historyLoading"
                      title="Re-fetch from GitHub">
                <i class="fa-solid fa-rotate mr-1"></i>Refresh
              </button>
            </div>
            <div *ngIf="historyExpanded" class="mt-1">
              <div *ngIf="historyLoading" class="text-xs text-gray-500 dark:text-gray-400 py-3">
                <i class="fa-solid fa-spinner fa-spin mr-1"></i>Fetching commits from GitHub…
              </div>
              <div *ngIf="!historyLoading && history.length === 0" class="text-xs text-gray-500 dark:text-gray-400 py-3">
                No commit history yet for this file path.
              </div>
              <table *ngIf="!historyLoading && history.length" class="w-full text-[11px] border border-gray-200 dark:border-[#434863] rounded">
                <thead class="bg-gray-50 dark:bg-app-black-100/40 text-[10px] uppercase text-gray-500 dark:text-gray-400">
                  <tr>
                    <th class="text-left px-2 py-1 border-b border-gray-200 dark:border-[#434863]">When</th>
                    <th class="text-left px-2 py-1 border-b border-gray-200 dark:border-[#434863]">Author</th>
                    <th class="text-left px-2 py-1 border-b border-gray-200 dark:border-[#434863]">Message</th>
                    <th class="text-right px-2 py-1 border-b border-gray-200 dark:border-[#434863]">SHA</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let c of history" class="border-b border-gray-100 dark:border-[#434863]/50 hover:bg-gray-50 dark:hover:bg-app-black-100/30">
                    <td class="px-2 py-1 whitespace-nowrap font-mono text-[10px] text-gray-600 dark:text-gray-300">{{ c.at | date:'yyyy-MM-dd HH:mm' }}</td>
                    <td class="px-2 py-1 truncate max-w-[200px]" [title]="c.author_email">
                      {{ c.author_name }}
                      <span *ngIf="c.author_email" class="text-gray-400 text-[10px]">({{ c.author_email }})</span>
                    </td>
                    <td class="px-2 py-1">{{ c.message }}</td>
                    <td class="px-2 py-1 text-right">
                      <a *ngIf="c.html_url" [href]="c.html_url" target="_blank" rel="noopener"
                         class="font-mono text-[10px] text-blue-600 dark:text-blue-400 hover:underline"
                         [title]="c.sha">
                        {{ c.sha.slice(0, 7) }}<i class="fa-solid fa-arrow-up-right-from-square ml-1 text-[9px]"></i>
                      </a>
                      <span *ngIf="!c.html_url" class="font-mono text-[10px] text-gray-500" [title]="c.sha">{{ c.sha.slice(0, 7) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-2 px-3 py-2 border-t border-gray-200 dark:border-[#434863] bg-gray-50 dark:bg-app-black-100 flex-none">
          <button
            class="dark-btn dark-btn-primary dark-btn-sm"
            (click)="save()"
            [disabled]="saving || !canSave">
            <i class="fa-solid fa-floppy-disk mr-1"></i>
            <span *ngIf="!saving">{{ mode === 'new' ? 'Create + refresh' : 'Save + refresh' }}</span>
            <span *ngIf="saving">Saving…</span>
          </button>
          <button
            class="dark-btn dark-btn-sm"
            (click)="revert()"
            [disabled]="saving || !dirty">
            <i class="fa-solid fa-rotate-left mr-1"></i>Revert
          </button>
          <button
            *ngIf="mode === 'view'"
            class="dark-btn dark-btn-danger dark-btn-sm ml-2"
            (click)="remove()"
            [disabled]="deleting">
            <i class="fa-solid fa-trash mr-1"></i>
            <span *ngIf="!deleting">Delete</span>
            <span *ngIf="deleting">Deleting…</span>
          </button>
          <button class="dark-btn dark-btn-sm ml-auto" (click)="cancelEdit()">
            Cancel
          </button>
        </div>
      </div>

    </div>
  </div>
</div>
